import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import type { PreviewValueTone } from '@/features/legends/preview/preview-values'
import { match } from '@/utils/pattern-match-util'
import styles from './PreviewOutput.module.css'

export interface PreviewOutputProps {
  title: string
  /**
   * Shown after the title, like a variable's ID.
   */
  caption?: string | null
  children?: ReactNode
  testId?: string
}

/**
 * One thing the engine computed, as a section of the outputs pane.
 */
export function PreviewOutput({
  title,
  caption,
  children,
  testId,
}: PreviewOutputProps) {
  return (
    <div className={styles.root} data-test={testId}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {caption && <span className={styles.caption}>{caption}</span>}
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  )
}

export interface PreviewValueTextProps {
  children?: ReactNode
  tone: PreviewValueTone
  /**
   * For an output's single value, as opposed to an entry of a list.
   */
  isLarge?: boolean
}

/**
 * A computed value, colored by its type.
 */
export function PreviewValueText({
  children,
  tone,
  isLarge,
}: PreviewValueTextProps) {
  return (
    <span
      className={cx(
        styles.value,
        match(tone, {
          String: styles.string,
          Number: styles.number,
          Boolean: styles.boolean,
          Date: styles.date,
          Other: undefined,
        }),
        isLarge && styles.large,
      )}
    >
      {children}
    </span>
  )
}

export interface PreviewLineProps {
  title?: string
  children?: ReactNode
  /**
   * Packs the line with its siblings, without a divider.
   */
  isCompact?: boolean
}

/**
 * An entry of a list output.
 */
export function PreviewLine({ title, children, isCompact }: PreviewLineProps) {
  return (
    <div className={cx(styles.line, isCompact && styles.compactLine)}>
      {title && <p className={styles.lineTitle}>{title}</p>}
      {children && <div className={styles.lineContent}>{children}</div>}
    </div>
  )
}

export interface PreviewStatProps {
  children?: ReactNode
}

/**
 * A figure on its own row of a line's content, like a quantity.
 */
export function PreviewStat({ children }: PreviewStatProps) {
  return <span className={styles.stat}>{children}</span>
}

export interface PreviewMatchListProps {
  children?: ReactNode
}

/**
 * A group of routing matches.
 */
export function PreviewMatchList({ children }: PreviewMatchListProps) {
  return <div className={styles.matches}>{children}</div>
}

export interface PreviewMatchProps {
  title: string
  description?: string
  isMatch: boolean
  testId?: string
}

/**
 * Whether a pro with the sample skills matches routing requirements or a
 * routing plan.
 */
export function PreviewMatch({
  title,
  description,
  isMatch,
  testId,
}: PreviewMatchProps) {
  return (
    <div
      className={cx(styles.match, isMatch && styles.matched)}
      data-test={testId}
    >
      <div className={styles.matchContent}>
        <p className={styles.matchTitle}>{title}</p>
        {description && (
          <p className={styles.matchDescription}>{description}</p>
        )}
      </div>
      <span
        className={styles.matchIcon}
        title={isMatch ? 'Matches' : 'Does not match'}
      >
        <Icon name="fire" size={16} />
      </span>
    </div>
  )
}

export interface PreviewNoticeProps {
  title: string
  children?: ReactNode
}

/**
 * A problem with the computed outputs.
 */
export function PreviewNotice({ title, children }: PreviewNoticeProps) {
  return (
    <div className={styles.notice}>
      <Icon name="status-alert" size={16} />
      <div>
        <p className={styles.noticeTitle}>{title}</p>
        <p className={styles.noticeText}>{children}</p>
      </div>
    </div>
  )
}

export interface PreviewMutedTextProps {
  children?: ReactNode
}

/**
 * Text that stands in for a missing value.
 */
export function PreviewMutedText({ children }: PreviewMutedTextProps) {
  return <span className={styles.muted}>{children}</span>
}
